import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getBatchRealtime, getStatisticsOverview, getDeviceList, getWorkshopList } from '@/api/heating'
import { heatingWs, type WebSocketMessage } from '@/api/websocket'

export const useHeatingStore = defineStore('heating', () => {
  // 设备实时状态 { deviceId: statusData }
  const deviceStatusMap = ref<Record<string, any>>({})
  // 设备基础信息列表
  const deviceList = ref<any[]>([])
  // 车间列表
  const workshopList = ref<any[]>([])
  // 统计概览
  const statistics = ref({
    totalDevices: 0,
    onlineCount: 0,
    runningCount: 0,
    offlineCount: 0,
    totalPower: 0,
    alarmCount: 0
  })

  // 在线设备数
  const onlineDevices = computed(() =>
    deviceList.value.filter(d => deviceStatusMap.value[d.deviceId]?.online === 1)
  )

  // 加载设备列表
  async function loadDevices() {
    try {
      const res = await getDeviceList({ pageNum: 1, pageSize: 200 })
      deviceList.value = res?.records || res || []
    } catch (e) {
      console.error('Failed to load devices', e)
    }
  }

  // 加载车间
  async function loadWorkshops() {
    try {
      workshopList.value = await getWorkshopList() as any || []
    } catch (e) {
      console.error('Failed to load workshops', e)
    }
  }

  // 加载统计数据
  async function loadStatistics() {
    try {
      statistics.value = await getStatisticsOverview() as any
    } catch (e) {
      console.error('Failed to load statistics', e)
    }
  }

  // 加载全部设备实时数据
  async function loadAllRealtime() {
    try {
      const list = await getBatchRealtime() as any[]
      if (Array.isArray(list)) {
        list.forEach((s: any) => {
          if (s.deviceId) {
            deviceStatusMap.value[s.deviceId] = s
          }
        })
      }
    } catch (e) {
      console.error('Failed to load realtime data', e)
    }
  }

  // 初始化 WebSocket
  function initWebSocket() {
    heatingWs.onMessage((msg: WebSocketMessage) => {
      if (msg.type === 'device_data' && msg.deviceId) {
        deviceStatusMap.value[msg.deviceId] = {
          ...deviceStatusMap.value[msg.deviceId],
          ...msg.data,
          online: 1
        }
      }
    })
    heatingWs.connect()
  }

  // 初始化全部数据
  async function init() {
    await Promise.all([loadDevices(), loadWorkshops(), loadStatistics(), loadAllRealtime()])
    initWebSocket()
  }

  return {
    deviceStatusMap,
    deviceList,
    workshopList,
    statistics,
    onlineDevices,
    loadDevices,
    loadWorkshops,
    loadStatistics,
    loadAllRealtime,
    initWebSocket,
    init
  }
})
